// apps/roleforge-app/src/components/audit/composeActivationData.ts
//
// Plan 3e-ii Task 5.7 — Pure data-mapping helper. Composes the
// `ActivationMomentLayoutProps` from the raw audit + team + opps + recs +
// roi-input rows so the page can stay thin and the mapping is unit-testable
// in isolation (no React, no fetch).

import { computeRoi, type RoiInputRow } from '@vibecontrol/shared/roleforge';
import type {
  AuditRow,
  OpsCanvasEngineRow,
  Opportunity,
  RecommendationRow,
  RoiInputRowDb,
} from '../../lib/api-client';
import type { AuditTeamRecommendation } from '../../lib/materialize-api';
import type { ActivationMomentLayoutProps } from './ActivationMomentLayout';

const OPERATIONS_MANAGER_ROLE_TEMPLATE_ID = 'platform-role-operations-manager';

/**
 * `firstTask` synthesis (per Plan 3e-ii spec, §5.7): the recommendation row
 * has no first-task field. We try to derive a sensible one from the metadata
 * (role assignment hints), and otherwise fall back to a generic-but-truthful
 * phrase. Adding a DB column for this is out of scope.
 */
function deriveFirstTask(
  rec: AuditTeamRecommendation,
  opps: Opportunity[],
): string {
  // Best-effort: find an opportunity whose metadata role_assignment matches
  // either the recommendation id or its name.
  const match = opps.find((o) => {
    const m = o.metadata as Record<string, unknown> | undefined;
    const role = typeof m?.role_assignment === 'string' ? m.role_assignment : null;
    if (!role) return false;
    return role === rec.id || role === rec.name;
  });
  if (match) return match.name;
  return 'Continues your operating-model journey…';
}

function deriveAgentName(rec: AuditTeamRecommendation): string {
  const bespoke = rec.bespoke_role as Record<string, unknown> | null | undefined;
  const bespokeName = typeof bespoke?.name === 'string' ? bespoke.name : null;
  return bespokeName ?? rec.name;
}

function deriveAgentRole(rec: AuditTeamRecommendation): string {
  const bespoke = rec.bespoke_role as Record<string, unknown> | null | undefined;
  const role = typeof bespoke?.role === 'string' ? bespoke.role : null;
  return role ?? rec.name;
}

function normalizeRoleName(value: unknown): string {
  return typeof value === 'string'
    ? value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
    : '';
}

function isOperationsManagerRecommendation(
  rec: AuditTeamRecommendation | RecommendationRow,
): boolean {
  const bespoke = rec.bespoke_role as Record<string, unknown> | null | undefined;
  const customizations = rec.customizations as Record<string, unknown> | null | undefined;
  const names = [rec.name, bespoke?.name, customizations?.name].map(normalizeRoleName);
  return rec.role_template_id === OPERATIONS_MANAGER_ROLE_TEMPLATE_ID
    || names.some((name) => name === 'operations manager' || name === 'operation manager' || name === 'ops manager');
}

function filterOperationsManagerRows<T extends AuditTeamRecommendation | RecommendationRow>(rows: T[]): T[] {
  const filtered = rows.filter((rec) => !isOperationsManagerRecommendation(rec));
  return filtered.length === rows.length ? rows : filtered;
}

/**
 * Convert the DB-shaped `RoiInputRowDb` rows to the shared `RoiInputRow`
 * shape that `computeRoi` accepts. `RoiInputRowDb` already extends
 * `RoiInputRow`, so this is a structural pass-through.
 */
function toRoiInputRows(rows: RoiInputRowDb[]): RoiInputRow[] {
  return rows;
}

function composeEngineLabels(rows: OpsCanvasEngineRow[] = []): Record<string, string> {
  const labels: Record<string, string> = {};
  for (const row of rows) {
    labels[row.id] = row.name;
    labels[row.engine] = row.name;
  }
  return labels;
}

export function composeActivationData(args: {
  audit: AuditRow;
  teamRecs: AuditTeamRecommendation[];
  opportunities: Opportunity[];
  recommendations: RecommendationRow[];
  roiInputs: RoiInputRowDb[];
  opsCanvas?: OpsCanvasEngineRow[];
}): ActivationMomentLayoutProps {
  const specialistTeamRecs = filterOperationsManagerRows(args.teamRecs);
  const top3 = specialistTeamRecs.slice(0, 3);
  const team = top3.map((rec) => ({
    agentName: deriveAgentName(rec),
    role: deriveAgentRole(rec),
    firstTask: deriveFirstTask(rec, args.opportunities),
  }));

  const breakdown = computeRoi(toRoiInputRows(args.roiInputs));
  const floorCents = breakdown.cost_savings.annual_cents;
  const ceilingCents =
    breakdown.revenue_uplift.annual_cents > 0
      ? breakdown.cost_savings.annual_cents + breakdown.revenue_uplift.annual_cents
      : null;

  return {
    auditId: args.audit.id,
    team,
    roi: { floorCents, ceilingCents },
    opportunities: args.opportunities,
    engineLabels: composeEngineLabels(args.opsCanvas),
    recommendations: filterOperationsManagerRows(args.recommendations),
    audit: args.audit,
    starterSkills: [],
  };
}
